import React from 'react';
import styled, { css } from 'styled-components';
import { Link as RouterLink } from 'react-router-dom';
import { colors } from 'global/colors';

export const Hr = styled.hr`
  border: 0;
  height: 1px;
  background: ${colors.lightGray2};
  width: 100%;
  margin: 0 0 2px;
`;

export const Wrapper = styled.div`
  background: ${(props) => props.color || colors.white};
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: ${(props) => (props.padding ? props.padding : 0)};
  min-height: ${(props) => (props.minHeight ? props.minHeight : 'calc(100% - 320px)')};

  :after {
    content: '';
    flex: auto;
  }

  @media print {
    padding: 0;
  }
`;

export const Container = styled.div`
  width: 80%;
  max-width: 1200px;
  padding: 30px 0;
  display: flex;
  justify-content: space-between;

  @media (max-width: 1366px) {
    width: 95%;
  }

  ${(props) =>
    props.color &&
    css`
      background: ${props.color};
      border-radius: 10px;
      padding: 20px;
      margin-top: 50px;
      margin-bottom: 50px;
      box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.3);
    `}

  ${(props) =>
    props.half &&
    css`
      width: 50%;
    `}

  ${(props) =>
    props.wide &&
    css`
      @media (max-width: 1024px) {
        flex-direction: column;
      }
    `}

  @media (max-width: 667px) {
    flex-direction: column;
  }

  ${(props) =>
    props.column &&
    css`
      flex-direction: column;
    `}

  ${(props) =>
    props.full &&
    css`
      max-width: none;
      width: 100% !important;
    `}

  ${(props) =>
    props.center &&
    css`
      justify-content: center;
    `}
  
  ${(props) =>
    props.column &&
    props.center &&
    css`
      align-items: center;
    `}

  ${(props) =>
    props.padding &&
    css`
      padding: 80px 0;

      @media (max-width: 1024px) {
        padding: 30px 0;
      }
    `}

    ${(props) =>
    props.usePadding &&
    css`
      padding: ${props.usePadding};
    `}
`;

export const Button = ({ children, type, ...props }) => (
  <ButtonContainer
    {...props}
    isMenuButton={props.isMenu && props.onClick}
    onClick={() => (props.buttonClick && !props.disabled ? props.buttonClick() : null)}
  >
    <ButtonElement type={type} {...props}>
      {children}
    </ButtonElement>
  </ButtonContainer>
);

export const LinkButton = ({ children, href, ...props }) => (
  <AnchorLink {...props} href={href}>
    <Button {...props}>{children}</Button>
  </AnchorLink>
);

const AnchorLink = styled.a`
  color: ${colors.white};
  margin: ${(props) => (props.removeMargin ? '0' : 'auto')};
  width: ${(props) => (props.width ? `${props.width}%` : null)};
  text-align: ${(props) => (props.textAlign ? `${props.textAlign}` : null)};
  padding: ${(props) => (props.usePadding ? `${props.usePadding}` : '0')};
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 1px;
  text-decoration: none;

  ${(props) =>
    props.useMargin &&
    css`
      margin: ${props.useMargin};
    `}

  :hover {
    color: ${colors.green};
    text-decoration: none;
  }
`;

export const Form = styled.form.attrs(({ onSubmit }) => ({
  onSubmit: (e) => {
    e.preventDefault();
    onSubmit();
  },
}))`
  display: flex;
  flex-direction: column;
  user-select: none;
`;

const ButtonContainer = styled.div`
  margin: ${(props) => (props.removeMargin ? '0' : 'auto')};
  width: ${(props) => (props.width ? `${props.width}%` : null)};
  text-align: ${(props) => (props.textAlign ? `${props.textAlign}` : null)};
  padding: ${(props) => (props.usePadding ? `${props.usePadding}` : '0')};

  ${(props) =>
    props.zIndex &&
    css`
      z-index: ${props.zIndex};
    `}

  ${(props) =>
    props.noVerticalMargin &&
    css`
      margin: 0 auto;
    `}

  ${(props) =>
    props.isMenuButton &&
    css`
      margin: 0 0 0 20px;
      @media (max-width: 1024px) {
        margin-left: 0;
      }
    `}
  ${(props) =>
    props.disabled &&
    css`
      cursor: not-allowed;
    `}

    ${(props) =>
    props.margin &&
    css`
      margin: ${props.margin};
    `}
`;

const ButtonElement = styled.button`
  text-transform: uppercase;
  padding: 7px 15px;
  border-radius: 25px;
  cursor: pointer;
  font-weight: 600;
  letter-spacing: 1px;
  color: ${colors.white};
  transition: padding 0.25s ease-in-out;
  border: 2px solid transparent;
  background: transparent;
  user-select: none;
  appearance: none !important;
  white-space: nowrap;

  :focus {
    outline: none;
    background: ${colors.fsCyan};
    color: ${colors.white};
    text-decoration: none;
  }

  :hover {
    background: ${colors.white};
    color: ${colors.green};
    text-decoration: none;
  }

  ${(props) =>
    props.isScrolled &&
    css`
      padding-top: 3px;
      padding-bottom: 3px;
    `}

  ${(props) =>
    props.primary &&
    css`
      background: ${colors.green};

      ${props.border &&
      css`
        :hover {
          border: 2px solid ${colors.green};
        }
      `}
    `}

  ${(props) =>
    props.secondary &&
    css`
      border-color: ${colors.white};
    `}

  ${(props) =>
    props.primaryBlue &&
    css`
      background: ${colors.darkBlue};
    `}

  ${(props) =>
    props.primaryGreenBorder &&
    css`
      :hover {
        border: 2px solid ${colors.green};
      }
    `}

    ${(props) =>
    props.primaryRed &&
    css`
      background: ${colors.red};

      :hover {
        border: 2px solid ${colors.red};
        color: ${colors.red};
      }
    `}

  ${(props) =>
    props.small &&
    css`
      padding: 6px 18px;
      font-size: 12px;
    `}

  ${(props) =>
    props.isMenu &&
    css`
      @media (max-width: 1024px) {
        background: none;
        border: none;
        padding: 0;
        color: ${colors.darkGray2};

        :hover {
          background: none;
        }
      }
    `}

  ${(props) =>
    props.disabled &&
    css`
      pointer-events: none;
      opacity: 0.5;
    `}

  ${(props) =>
    props.padding &&
    css`
      padding: ${props.padding};
    `}

    ${(props) =>
    props.fontSize &&
    css`
      font-size: ${props.fontSize};
    `}

    ${(props) =>
    props.primaryGreenBorder &&
    css`
      :hover {
        border: 2px solid ${colors.green};
      }
    `}
`;

export const Link = styled(RouterLink)`
  text-decoration: none;
  color: unset;

  :hover {
    color: unset;
    text-decoration: none;
  }
`;

export const A = styled.a`
  text-decoration: none;
  color: unset;

  :hover {
    color: unset;
    text-decoration: none;
  }
`;

export const Section = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;
  z-index: 0;

  ${(props) =>
    props.dark &&
    css`
      background: ${colors.darkBlue};
    `}

  ${(props) =>
    props.backgroundImage &&
    css`
      background-image: url(${props.backgroundImage});
      background-size: cover;
      background-position: center center;
      background-repeat: no-repeat;
      min-height: 600px;
    `}
`;

export const Header = styled.h1`
  color: ${colors.darkBlue};
  font-weight: 600;
  margin-bottom: 20px;
`;

export const SectionHeader = styled.span`
  text-transform: uppercase;
  font-weight: 300;
  font-size: 30px;
  margin-bottom: 35px;
  color: ${colors.darkBlue};
  ${(props) =>
    props.margin &&
    css`
      margin-top: 100px;
    `}
  ${(props) =>
    props.bold &&
    css`
      font-weight: bold;
    `}
    ${(props) =>
    props.large &&
    css`
      font-size: 40px;
    `}
    ${(props) =>
    props.lightColor &&
    css`
      color: ${colors.white};
    `};
`;

export const Input = styled.input`
  font-size: 16px;
  box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.1);
  padding: 10px 15px;
  border: none;
  border-radius: 5px;
  background: ${colors.white};
  outline: none;
  color: ${colors.darkGray};
  margin-bottom: 15px;

  :focus {
    box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.25);
  }
`;

export const InfoText = styled.span`
  color: #555555;
  font-size: 12px;
  margin-bottom: 10px;

  ${(props) =>
    props.error &&
    css`
      color: red;
    `}

  ${(props) =>
    props.success &&
    css`
      color: green;
    `}
  
  ${(props) =>
    props.medium &&
    css`
      font-size: 14px;
    `}
`;

export const Checkbox = ({ checked, label, right, onChange }) => {
  const input = <CheckboxInput right={right} checked={checked} onChange={onChange} />;
  const inputLabel = <Label right={right}>{label}</Label>;

  return (
    <CheckboxContainer>
      {right ? (
        <label>
          {input}
          {inputLabel}
        </label>
      ) : (
        <label>
          {inputLabel}
          {input}
        </label>
      )}
    </CheckboxContainer>
  );
};

const CheckboxContainer = styled.div``;

export const CheckboxInput = styled.input.attrs({ type: 'checkbox' })`
  outline: none;

  ${(props) =>
    !props.right &&
    css`
      margin-left: 5px;
    `}
`;

export const Label = styled.span`
  font-size: 14px;
  color: ${colors.darkGray};

  ${(props) =>
    props.right &&
    css`
      margin-left: 5px;
    `}
`;
